import { DragDropContext } from '@hello-pangea/dnd';
import { Formik } from 'formik';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppTour } from '@/components/common/AppTour';
import CustomizeModal from '@/components/customize/CustomizeModal';
import { Spinner } from '@/components/icons';
import { KeyboardShortcutsModal } from '@/components/modals/KeyboardShortcutsModal';
import { listBlockGroups, moveBlockGroup } from '@/components/preview/block-selection/blockGroups';
import { BlockSelectionToolbar } from '@/components/preview/block-selection/BlockSelectionToolbar';
import {
    DropIndicatorProvider,
    type DropIndicatorTarget,
} from '@/components/preview/block-selection/DropIndicatorContext';
import { MarqueeSelectionLayer } from '@/components/preview/block-selection/MarqueeSelectionLayer';
import { TestBlockSelectionProvider } from '@/components/preview/block-selection/TestBlockSelectionContext';
import { useHighlightActiveSection } from '@/components/preview/hooks/useHighlightActiveSection';
import ReportPreview from '@/components/preview/ReportPreview';
import { Button } from '@/components/ui/button';
import { useModalContext } from '@/components/ui/modal-provider';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import ChartConfigModal from '@/components/wizard/ChartConfigModal';
import { ReportMeasurementProvider } from '@/context/ReportMeasurementContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { useReportSections } from '@/hooks/use-report-sections';
import { toReportStepKey } from '@/lib/analytics-events';
import { capture } from '@/lib/posthog';
import { getCustomSectionById, resolveReportSectionOrder } from '@/lib/report-sections';
import { isCompletedReport } from '@/utils';
import { AllCommentsModalContent } from './comments/components/AllCommentsModalContent';
import { CreateCommentDialog } from './comments/components/CreateCommentDialog';
import { useReportComments } from './comments/hooks/use-report-comments';
import {
    CommentableSectionId,
    getCommentCountsBySection,
    getDefaultCommentSectionId,
    getTotalActiveCommentCount,
    isCommentableSectionId,
} from './comments/utils';
import { Header } from './components/Header';
import { PanelScopeSync } from './components/PanelScopeSync';
import { PreviewActivityLayer } from './components/PreviewActivityLayer';
import { ReportImportsDock } from './components/ReportImportsDock';
import { ReportZoomSurface } from './components/ReportZoomSurface';
import { SidebarTogglePill } from './components/SidebarTogglePill';
import { StepContentSidebar } from './components/StepContentSidebar';
import { StepsSidebar } from './components/StepsSidebar';
import { CREATE_REPORT_TOUR_KEY, tourSteps } from './config/tour';
import { INITIAL_FORM_DATA } from './constants';
import { ReportWizardProvider, useReportWizard } from './context/ReportWizardContext';
import { ZoomProvider } from './context/ZoomContext';
import { CoverView } from './cover/CoverView';
import { COVER_VIEW_PARAM, COVER_VIEW_VALUE } from './cover/use-cover-view-state';
import { useDragAutoScroll } from './hooks/useDragAutoScroll';
import { useReportDragAndDrop } from './hooks/useReportDragAndDrop';
import { useReportShortcuts } from './hooks/useReportShortcuts';
import { useReportSidebar } from './hooks/useReportSidebar';
import { useReportTests } from './hooks/useReportTests';
import type { PublisherResultImport } from '@/types/publisher-result-import';
import type { DropResult, DragStart, DragUpdate } from '@hello-pangea/dnd';

function PendingPublisherResultImportsBanner({
    imports,
    applyingImportId,
    onApply,
}: {
    imports: PublisherResultImport[];
    applyingImportId: string | null;
    onApply: (importId: string) => Promise<void>;
}) {
    if (imports.length === 0) return null;

    const nextImport = imports[0];
    const isApplying = applyingImportId === nextImport.id;

    return (
        <div className="border-b border-info/20 bg-info-subtle p-3 md:px-6">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                    <p className="text-sm font-semibold text-foreground">
                        {imports.length === 1
                            ? '1 resultado importado aguarda este laudo'
                            : `${imports.length} resultados importados aguardam este laudo`}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                        Aplique as tabelas aprovadas ao laudo em andamento. Próximo arquivo:{' '}
                        {nextImport.original_filename}
                    </p>
                </div>
                <Button
                    type="button"
                    size="sm"
                    onClick={() => void onApply(nextImport.id)}
                    disabled={Boolean(applyingImportId)}
                    className="self-start md:self-auto"
                >
                    {isApplying && <Spinner className="size-4 animate-spin" />}
                    Aplicar ao laudo
                </Button>
            </div>
        </div>
    );
}

/**
 * Drags that get a drop indicator: a new block coming out of the toolbox, or
 * an existing block being reordered inside the paginated canvas. Section and
 * battery drags are excluded — they have their own affordances.
 */
const isDropIndicatorSource = (droppableId: string) =>
    droppableId === 'toolbox' || droppableId.startsWith('test_blocks_');

function CreateReportContent() {
    const { openModal, closeModal, isOpen: isModalOpen } = useModalContext();
    const isMobile = useIsMobile();
    const [searchParams] = useSearchParams();
    const isCoverView = searchParams.get(COVER_VIEW_PARAM) === COVER_VIEW_VALUE;
    const {
        formData,
        currentStepIndex,
        sidebarSteps,
        setCurrentStepIndex,
        handleUpdate,
        commitHistory,
        handleSave,
        canSaveReport,
        handleUndo,
        handleRedo,
        handleExportJson,
        showCustomizeModal,
        setShowCustomizeModal,
        customizeSection,
        totalPages,
        setTotalPages,
        setSectionPageMap,
        handleSectionClick,
        consumeStepViewIntent,
        completionAttempt,
        isLoading,
        scrollTrigger,
        hasSeenCreateReportTour,
        hasPendingTemplatePrompt,
        isPendingPatientConfirmation,
        handleCreateReportTourComplete,
        openPendingTemplatePrompt,
        pendingPublisherResultImports,
        applyingPublisherResultImportId,
        applyPendingPublisherResultImport,
        commentDraftSectionId,
        clearCommentDraft,
        setPendingEditBlockId,
        spotlightAnchorId,
        setSpotlightAnchorId,
        setPanelScope,
    } = useReportWizard();

    const isReadOnly = isCompletedReport(formData);

    const [isCreateCommentDialogOpen, setIsCreateCommentDialogOpen] = useState(false);

    // "Comentar nesta seção" in the canvas's right-click menu only sets the
    // draft; opening the dialog is this side of the bridge.
    useEffect(() => {
        if (commentDraftSectionId) setIsCreateCommentDialogOpen(true);
    }, [commentDraftSectionId]);

    const handleCreateCommentDialogOpenChange = useCallback(
        (open: boolean) => {
            setIsCreateCommentDialogOpen(open);
            if (!open) clearCommentDraft();
        },
        [clearCommentDraft],
    );

    const {
        sidebarWidth,
        isResizing,
        isLeftSidebarCollapsed,
        setIsLeftSidebarCollapsed,
        isSidebarExpanded,
        isRightSidebarCollapsed,
        setIsRightSidebarCollapsed,
        isRightSidebarExpanded,
        isSnappingRightSidebar,
        handleRightSidebarResizeStart,
    } = useReportSidebar(completionAttempt);

    const [isMobileStepsOpen, setIsMobileStepsOpen] = useState(false);
    const [isMobileToolsOpen, setIsMobileToolsOpen] = useState(false);

    // "Tour do editor" in the help menu. Joyride only starts on a false → true
    // `run`, so each replay remounts AppTour with a fresh key.
    const [tourReplayCount, setTourReplayCount] = useState(0);
    const replayTour = useCallback(() => setTourReplayCount((count) => count + 1), []);
    const rightSidebarWidth = !isMobile && isRightSidebarExpanded ? sidebarWidth : 0;
    const isDesktopLeftSidebarExpanded = !isMobile && isSidebarExpanded;

    const toggleStepsPanel = useCallback(() => {
        if (isMobile) {
            setIsMobileStepsOpen((prev) => !prev);
            return;
        }

        setIsLeftSidebarCollapsed((prev) => !prev);
    }, [isMobile, setIsLeftSidebarCollapsed]);

    const toggleToolsPanel = useCallback(() => {
        if (isMobile) {
            setIsMobileToolsOpen((prev) => !prev);
            return;
        }

        setIsRightSidebarCollapsed((prev) => !prev);
    }, [isMobile, setIsRightSidebarCollapsed]);

    useReportShortcuts(
        handleSave,
        canSaveReport,
        toggleStepsPanel,
        toggleToolsPanel,
        handleUndo,
        handleRedo,
        handleExportJson,
    );

    const {
        showChartConfig,
        closeChartConfig,
        lockChartSourceSelection,
        editingChartBlock,
        selectedChartTableId,
        availableChartTables,
        chartModalInitialConfig,
        handleAddTestsToBlock,
        openInstrumentSelector,
        handleAddChartFromTable,
        openChartConfigFromToolbox,
        handleAddBlock,
        handleEditChartBlock,
        handleSaveChartConfig,
    } = useReportTests(formData, handleUpdate, setPendingEditBlockId, setPanelScope);

    const { handleDragEnd: handleTestDragEnd } = useReportDragAndDrop(
        formData,
        handleUpdate,
        openChartConfigFromToolbox,
    );

    const [dropTarget, setDropTarget] = useState<Omit<DropIndicatorTarget, 'height'> | null>(null);
    // Height of the gap the drag opens = the dragged item's height (measured on start).
    const [dragHeight, setDragHeight] = useState(0);
    // Holds pagination steady while an in-canvas block drag is in flight (an
    // async measurement mid-drag would swap the droppable ids under dnd).
    const [isBlockDragActive, setIsBlockDragActive] = useState(false);

    const currentStep = sidebarSteps[currentStepIndex] || sidebarSteps[0];

    useHighlightActiveSection(currentStep?.id, { scrollTrigger });

    // The step funnel: where a laudo stops being worked on. Only the section
    // key, its position and how it was reached travel — never any of the
    // content being written, and never a custom section's per-report id
    // (toReportStepKey buckets those). The ref is keyed on the step id, not the
    // key: moving between two adjacent custom sections is two real step views.
    //
    // `via` is consumed after the dedupe guard so a StrictMode re-invocation
    // cannot eat the intent before the event that needs it is emitted.
    const lastTrackedStepId = useRef<string | null>(null);
    const isFirstStepViewRef = useRef(true);
    useEffect(() => {
        const stepId = currentStep?.id;
        if (!stepId || lastTrackedStepId.current === stepId) return;

        const stepKey = toReportStepKey(stepId, currentStep?.isCustom);
        if (!stepKey) return;

        lastTrackedStepId.current = stepId;

        const via = isFirstStepViewRef.current ? 'initial' : consumeStepViewIntent(stepId);
        isFirstStepViewRef.current = false;

        capture('report_wizard_step_viewed', {
            step_key: stepKey,
            step_index: currentStepIndex,
            via,
        });
    }, [consumeStepViewIntent, currentStep?.id, currentStep?.isCustom, currentStepIndex]);

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const { startAutoScroll, stopAutoScroll } = useDragAutoScroll({
        scrollContainerRef,
    });
    const { comments } = useReportComments(formData.id);
    const reportSections = useReportSections(formData);

    const totalActiveCommentCount = useMemo(() => getTotalActiveCommentCount(comments), [comments]);

    const commentCountsBySection = useMemo(() => getCommentCountsBySection(comments), [comments]);

    const commentSectionOptions = useMemo(
        () =>
            reportSections.reduce<Array<{ id: CommentableSectionId; title: string }>>(
                (accumulator, section) => {
                    if (!isCommentableSectionId(section.id)) {
                        return accumulator;
                    }

                    accumulator.push({
                        id: section.id,
                        title: section.title,
                    });

                    return accumulator;
                },
                [],
            ),
        [reportSections],
    );

    const currentCommentSectionId = currentStep?.id;

    const handleSectionDragEnd = useCallback(
        (result: DropResult) => {
            const { source, destination } = result;
            if (!destination) return;
            if (
                source.droppableId !== 'report_sections' ||
                destination.droppableId !== 'report_sections'
            ) {
                return;
            }

            const activeStepId = currentStep?.id;
            let nextOrder: string[] = [];

            handleUpdate((prev) => {
                const order = resolveReportSectionOrder(prev);
                const [movedSectionId] = order.splice(source.index, 1);
                if (!movedSectionId || !getCustomSectionById(prev, movedSectionId)) {
                    return {};
                }

                order.splice(destination.index, 0, movedSectionId);
                nextOrder = order;

                return { section_order: order };
            });

            if (activeStepId) {
                const nextBodyIndex = nextOrder.indexOf(activeStepId);
                if (nextBodyIndex !== -1) {
                    setCurrentStepIndex(nextBodyIndex + 1);
                }
            }
        },
        [currentStep?.id, handleUpdate, setCurrentStepIndex],
    );

    /**
     * The battery list in the tools panel, reordered by its grip handles. The
     * rows are groups, not blocks, so the move goes through `moveBlockGroup`
     * (the whole run travels, the dividers stay put) rather than the block
     * mover the page drags use.
     */
    const handleBatteryDragEnd = useCallback(
        (result: DropResult) => {
            const { source, destination, draggableId } = result;
            if (!destination || destination.droppableId !== 'battery_groups') return;
            if (destination.index === source.index) return;

            handleUpdate((prev) => {
                const currentTests = prev.tests || { section_title: '', blocks: [] };
                const blocks = currentTests.blocks || [];
                const groupKey = draggableId.replace('battery-group-', '');

                // Resolved by id, not by `source.index`: the list is rebuilt
                // from the live blocks on every render. The destination index
                // needs no such care — the collapsed list is a *prefix* slice,
                // so a move inside it lands at the same index in the full list.
                const fromIndex = listBlockGroups(blocks).findIndex(
                    (group) => (group.anchorRealId ?? group.firstRealId) === groupKey,
                );
                if (fromIndex === -1) return {};

                const next = moveBlockGroup(blocks, fromIndex, destination.index);
                if (next === blocks) return {};

                return { tests: { ...currentTests, blocks: next } };
            });
        },
        [handleUpdate],
    );

    const handleEditorDragStart = useCallback(
        (start: DragStart) => {
            setDropTarget(null);

            // Measure the dragged item: dnd shifts the destination blocks by
            // exactly this height, so the drop slot fills that gap precisely
            // (no overlap). Measured for reorders too, not just toolbox cards —
            // leaving it at 0 for block drags rendered a zero-height indicator,
            // which is indistinguishable from no indicator at all.
            //
            // Deliberately the *viewport* rect and not `offsetHeight`: dnd
            // measures in viewport space and applies the result as a CSS
            // transform, so inside the zoomed canvas the gap it opens is the
            // viewport height, not the layout height. Matching dnd is what
            // keeps the slot flush at 50% and 150%.
            if (isDropIndicatorSource(start.source.droppableId)) {
                const node = document.querySelector(
                    `[data-rfd-draggable-id="${start.draggableId}"]`,
                );
                setDragHeight(node ? node.getBoundingClientRect().height : 0);
            } else {
                setDragHeight(0);
            }

            if (start.source.droppableId.startsWith('test_blocks_')) {
                setIsBlockDragActive(true);
            }

            if (start.source.droppableId === 'report_sections') return;
            startAutoScroll(start);
        },
        [startAutoScroll],
    );

    const handleEditorDragUpdate = useCallback((update: DragUpdate) => {
        const { source, destination } = update;

        // The indicator describes drops into the tests canvas: a toolbox
        // insert or a block reorder. Section and battery drags have their own
        // affordances and clear it.
        if (!isDropIndicatorSource(source.droppableId)) {
            setDropTarget(null);
            return;
        }

        // A null destination is almost always the paper gap between two pages:
        // page chunks are separate droppables and the gap belongs to none of
        // them. Clearing here blinked the cue out mid-gesture on every
        // multi-page laudo, so the last real edge is held instead — flagged
        // `pending`, because with no destination dnd has snapped the
        // neighbours back and there is no gap left to fill.
        if (!destination) {
            setDropTarget((prev) => (prev?.pending ? prev : prev && { ...prev, pending: true }));
            return;
        }

        // A destination outside the tests canvas is a real "not here".
        if (!destination.droppableId.startsWith('test_blocks_')) {
            setDropTarget(null);
            return;
        }

        // dnd reports the dragged item's FINAL index; the indicator renders on
        // the edge *before* a block. Within one chunk, any destination at or
        // past the source leaves the gap after that block, so shift by one.
        // Cross-chunk and toolbox drops are plain inserts and need no shift.
        //
        // The shift also keeps the indicator off the block being dragged: that
        // element is position:fixed under the cursor, and an indicator nested
        // inside it would fly along with it instead of marking the target.
        const sameList = source.droppableId === destination.droppableId;
        const landsAfterSource = sameList && destination.index >= source.index;

        setDropTarget({
            droppableId: destination.droppableId,
            index: destination.index + (landsAfterSource ? 1 : 0),
        });
    }, []);

    const handleEditorDragEnd = useCallback(
        (result: DropResult) => {
            stopAutoScroll();
            setDropTarget(null);
            setDragHeight(0);
            setIsBlockDragActive(false);

            if (
                result.source.droppableId === 'report_sections' ||
                result.destination?.droppableId === 'report_sections'
            ) {
                handleSectionDragEnd(result);
                return;
            }

            if (result.source.droppableId === 'battery_groups') {
                handleBatteryDragEnd(result);
                return;
            }

            handleTestDragEnd(result);
        },
        [handleBatteryDragEnd, handleSectionDragEnd, handleTestDragEnd, stopAutoScroll],
    );

    const getDefaultSectionId = () =>
        getDefaultCommentSectionId({
            currentSectionId: currentCommentSectionId,
            sectionIds: commentSectionOptions.map((section) => section.id),
            comments,
        });

    useEffect(() => {
        if (!isMobile) {
            setIsMobileStepsOpen(false);
            setIsMobileToolsOpen(false);
        }
    }, [isMobile]);

    useEffect(() => {
        if (isMobile && completionAttempt > 0) {
            setIsMobileStepsOpen(true);
        }
    }, [completionAttempt, isMobile]);

    const openAllCommentsModal = useCallback(() => {
        openModal({
            title: 'Comentários',
            content: (
                <AllCommentsModalContent
                    reportId={formData.id}
                    sectionOptions={commentSectionOptions}
                />
            ),
            size: 'xl',
            bodyClassName: 'h-full flex min-h-0 flex-1 flex-col overflow-hidden p-0',
            footer: (
                <div className="flex w-full justify-end">
                    <Button type="button" variant="outline" onClick={closeModal}>
                        Fechar
                    </Button>
                </div>
            ),
        });
    }, [closeModal, commentSectionOptions, formData.id, openModal]);

    const handleOpenNewComment = useCallback(() => {
        setIsCreateCommentDialogOpen(true);
    }, []);

    const handleOpenShortcuts = useCallback(() => {
        openModal({
            title: 'Atalhos de teclado',
            content: <KeyboardShortcutsModal />,
            size: 'lg',
        });
    }, [openModal]);

    useEffect(() => {
        // The provider holds one modal at a time, and opening this one consumes
        // the pending prompt — so if it won the race against the "create the
        // report for this patient?" question, it would replace a question that
        // can never be asked again. `isModalOpen` is too late to prevent that
        // (it only turns true once the other modal has committed); the pending
        // flag is derived during render, so it is already true here.
        if (
            isPendingPatientConfirmation ||
            isLoading ||
            isModalOpen ||
            !hasSeenCreateReportTour ||
            !hasPendingTemplatePrompt
        ) {
            return;
        }

        const timer = window.setTimeout(() => {
            openPendingTemplatePrompt();
        }, 300);

        return () => window.clearTimeout(timer);
    }, [
        hasPendingTemplatePrompt,
        hasSeenCreateReportTour,
        isLoading,
        isModalOpen,
        isPendingPatientConfirmation,
        openPendingTemplatePrompt,
    ]);

    // A capa é "uma página própria": sem o header do laudo, sem as sidebars,
    // com back button próprio (dentro do CoverView). Mesma rota (?view=capa).
    if (isCoverView) {
        return (
            <div className="flex h-screen flex-col bg-background" onBlurCapture={commitHistory}>
                <CoverView />
            </div>
        );
    }

    return (
        <DragDropContext
            onDragStart={handleEditorDragStart}
            onDragUpdate={handleEditorDragUpdate}
            onDragEnd={handleEditorDragEnd}
        >
            <AppTour
                key={tourReplayCount}
                steps={tourSteps}
                run={!isLoading}
                replay={tourReplayCount > 0}
                tourKey={CREATE_REPORT_TOUR_KEY}
                onComplete={handleCreateReportTourComplete}
            />
            <div className="flex h-screen flex-col bg-background" onBlurCapture={commitHistory}>
                <Header
                    activeCommentCount={totalActiveCommentCount}
                    onOpenNewComment={handleOpenNewComment}
                    onOpenAllComments={openAllCommentsModal}
                />

                {!isReadOnly && (
                    <PendingPublisherResultImportsBanner
                        imports={pendingPublisherResultImports}
                        applyingImportId={applyingPublisherResultImportId}
                        onApply={applyPendingPublisherResultImport}
                    />
                )}

                <CreateCommentDialog
                    open={isCreateCommentDialogOpen}
                    onOpenChange={handleCreateCommentDialogOpenChange}
                    reportId={formData.id}
                    initialSectionId={commentDraftSectionId ?? getDefaultSectionId()}
                    sectionOptions={commentSectionOptions}
                    onOpenAllComments={openAllCommentsModal}
                />

                <DropIndicatorProvider
                    value={dropTarget ? { ...dropTarget, height: dragHeight } : null}
                >
                    <TestBlockSelectionProvider
                        blocks={formData.tests?.blocks ?? []}
                        onUpdate={handleUpdate}
                        disabled={isReadOnly}
                        reportId={formData.id}
                    >
                        <div className="relative flex flex-1 overflow-hidden">
                            {!isMobile && (
                                <StepsSidebar
                                    isSidebarExpanded={isSidebarExpanded}
                                    setIsLeftSidebarCollapsed={setIsLeftSidebarCollapsed}
                                />
                            )}

                            {isMobile ? (
                                <SidebarTogglePill
                                    id="tour-steps-sidebar-pill"
                                    side="left"
                                    onClick={() => setIsMobileStepsOpen(true)}
                                    className="top-4 size-11 bg-background/95"
                                />
                            ) : isLeftSidebarCollapsed ? (
                                <SidebarTogglePill
                                    id="tour-steps-sidebar-pill"
                                    side="left"
                                    onClick={() => setIsLeftSidebarCollapsed(false)}
                                />
                            ) : null}

                            <Sheet open={isMobileStepsOpen} onOpenChange={setIsMobileStepsOpen}>
                                <SheetContent
                                    side="left"
                                    className="flex h-full w-[88vw] max-w-[340px] flex-col overflow-hidden bg-background p-0 sm:max-w-[340px] [&>button]:hidden"
                                >
                                    <SheetTitle className="sr-only">Navegação do laudo</SheetTitle>
                                    <StepsSidebar
                                        isSidebarExpanded
                                        isInSheet
                                        setIsLeftSidebarCollapsed={setIsLeftSidebarCollapsed}
                                        onClose={() => setIsMobileStepsOpen(false)}
                                        onStepSelect={() => setIsMobileStepsOpen(false)}
                                    />
                                </SheetContent>
                            </Sheet>

                            <div
                                ref={scrollContainerRef}
                                data-preview-container
                                // Touching the laudo puts out the instrument the
                                // sidebar list lit up. Capture phase, so it lands
                                // before the block's own click handlers; on
                                // pointerdown rather than scroll, so the user can
                                // scroll around and keep looking at the mark.
                                onPointerDownCapture={() => {
                                    if (spotlightAnchorId !== null) setSpotlightAnchorId(null);
                                }}
                                className="flex flex-1 items-start justify-center overflow-auto scroll-smooth bg-surface-muted pb-32 md:pb-48"
                            >
                                <ReportZoomSurface scrollContainerRef={scrollContainerRef}>
                                    <ReportPreview
                                        data={formData}
                                        onUpdate={handleUpdate}
                                        onTotalPagesChange={setTotalPages}
                                        onSectionPageMapChange={setSectionPageMap}
                                        onAddTest={handleAddTestsToBlock}
                                        onAddInstrument={openInstrumentSelector}
                                        freezePagination={isBlockDragActive}
                                        onAddChartFromTable={handleAddChartFromTable}
                                        onEditChart={handleEditChartBlock}
                                        onAddBlock={handleAddBlock}
                                        onSectionClick={handleSectionClick}
                                        disabled={isReadOnly}
                                        isLoading={isLoading}
                                        commentCountsBySection={commentCountsBySection}
                                        onOpenAllComments={openAllCommentsModal}
                                    />
                                </ReportZoomSurface>
                            </div>

                            <PreviewActivityLayer
                                containerRef={scrollContainerRef}
                                totalPages={totalPages}
                                rightSidebarWidth={rightSidebarWidth}
                                isSidebarExpanded={isDesktopLeftSidebarExpanded}
                                hideFloatingControls={isMobile}
                                onOpenShortcuts={handleOpenShortcuts}
                                onReplayTour={replayTour}
                            />

                            {!isReadOnly && (
                                <ReportImportsDock
                                    rightSidebarWidth={rightSidebarWidth}
                                    isMobile={isMobile}
                                />
                            )}

                            <PanelScopeSync />

                            <BlockSelectionToolbar
                                isSidebarExpanded={isDesktopLeftSidebarExpanded}
                                rightSidebarWidth={rightSidebarWidth}
                            />
                            <MarqueeSelectionLayer containerRef={scrollContainerRef} />

                            {!isMobile && (
                                <StepContentSidebar
                                    sidebarWidth={sidebarWidth}
                                    isResizing={isResizing}
                                    onRightSidebarResizeStart={handleRightSidebarResizeStart}
                                    isRightSidebarExpanded={isRightSidebarExpanded}
                                    isSnappingRightSidebar={isSnappingRightSidebar}
                                    setIsRightSidebarCollapsed={setIsRightSidebarCollapsed}
                                    currentStep={currentStep}
                                    handleAddBlock={handleAddBlock}
                                    disabled={isReadOnly}
                                />
                            )}

                            {isMobile ? (
                                <SidebarTogglePill
                                    id="tour-tools-sidebar-pill"
                                    side="right"
                                    onClick={() => setIsMobileToolsOpen(true)}
                                    className="top-4 size-11 bg-background/95"
                                />
                            ) : isRightSidebarCollapsed ? (
                                <SidebarTogglePill
                                    id="tour-tools-sidebar-pill"
                                    side="right"
                                    onClick={() => setIsRightSidebarCollapsed(false)}
                                />
                            ) : null}

                            <Sheet open={isMobileToolsOpen} onOpenChange={setIsMobileToolsOpen}>
                                <SheetContent
                                    side="right"
                                    className="flex h-full w-[94vw] max-w-[420px] flex-col overflow-hidden bg-background p-0 sm:max-w-[420px] [&>button]:hidden"
                                >
                                    <SheetTitle className="sr-only">
                                        Ferramentas da seção
                                    </SheetTitle>
                                    <StepContentSidebar
                                        sidebarWidth={sidebarWidth}
                                        isResizing={false}
                                        onRightSidebarResizeStart={() => {}}
                                        isRightSidebarExpanded
                                        isSnappingRightSidebar={false}
                                        setIsRightSidebarCollapsed={setIsRightSidebarCollapsed}
                                        currentStep={currentStep}
                                        handleAddBlock={handleAddBlock}
                                        disabled={isReadOnly}
                                        isInSheet
                                        onClose={() => setIsMobileToolsOpen(false)}
                                    />
                                </SheetContent>
                            </Sheet>
                        </div>
                    </TestBlockSelectionProvider>
                </DropIndicatorProvider>

                <CustomizeModal
                    open={showCustomizeModal}
                    onOpenChange={setShowCustomizeModal}
                    data={formData}
                    onUpdate={handleUpdate}
                    initialSection={customizeSection}
                />

                <ChartConfigModal
                    open={showChartConfig}
                    onOpenChange={(open: boolean) => {
                        if (!open) {
                            closeChartConfig();
                        }
                    }}
                    availableTables={availableChartTables}
                    selectedTableId={selectedChartTableId}
                    lockTableSelection={lockChartSourceSelection}
                    isEditing={!!editingChartBlock}
                    primaryColor={formData.customizations?.colors?.primary}
                    secondaryColor={formData.customizations?.colors?.secondary}
                    initialConfig={chartModalInitialConfig}
                    onConfirm={handleSaveChartConfig}
                />
            </div>
        </DragDropContext>
    );
}

export default function CreateReport() {
    return (
        <Formik
            initialValues={INITIAL_FORM_DATA}
            onSubmit={async (values) => {
                console.log('Form submitted:', values);
            }}
            enableReinitialize
        >
            <ReportWizardProvider>
                <ReportMeasurementProvider>
                    <ZoomProvider>
                        <CreateReportContent />
                    </ZoomProvider>
                </ReportMeasurementProvider>
            </ReportWizardProvider>
        </Formik>
    );
}
